import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { utils } from '@atlaskit/util-service-support';
import { timed } from './timing';
import { limitToMaxSiteUsage } from './multi-site-utils';
import { batchMethod } from './api-batch';
import { getFetchProductPermissions } from './enabled-scopes';

// Type used to help unpack the types that make up an array

// Type used to lookup the response based on the scope

export class AggregatorClient {
  constructor({
    url,
    cloudId,
    siteMasterList
  }) {
    _defineProperty(this, "getAggregatorConfig", () => {
      return {
        url: this.serviceConfig.url,
        cloudId: this.cloudId,
        siteMasterList: this.siteMasterList
      };
    });
    _defineProperty(this, "getGenericProductPermissions", async args => {
      return getFetchProductPermissions(this.makeGetRequest)(args.productIds, this.cloudId, args.experience);
    });
    _defineProperty(this, "batchedGetExtensibleProductPermission", batchMethod(args => {
      const emptyProductPermissions = {
        productIds: [],
        experience: ''
      };
      if (args.length === 0) {
        return emptyProductPermissions;
      }
      return args.reduce((acc, arg) => {
        return {
          productIds: [...acc.productIds, ...arg.productIds],
          experience: arg.experience
        };
      }, emptyProductPermissions);
    }, this.getGenericProductPermissions, (response, id) => response.get(id) || []));
    _defineProperty(this, "makePostRequest", (path, body) => {
      const options = {
        path,
        requestInit: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        }
      };
      return utils.requestService(this.serviceConfig, options);
    });
    _defineProperty(this, "makeGetRequest", path => {
      const options = {
        path,
        requestInit: {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      };
      return utils.requestService(this.serviceConfig, options);
    });
    this.serviceConfig = {
      url: url
    };
    this.cloudId = cloudId;
    this.siteMasterList = siteMasterList;
  }
  async getAbTestData(scope, experience, product) {
    const body = {
      cloudId: this.cloudId,
      scopes: [scope],
      experience,
      product
    };
    const response = await this.makePostRequest(AggregatorClient.EXPERIMENT_URL_PATH, body);
    const scopeWithAbTest = response.scopes.find(s => s.id === scope);
    const abTestPromise = scopeWithAbTest ? Promise.resolve(scopeWithAbTest.abTest) : Promise.resolve(null);
    return abTestPromise;
  }

  /**
   * Get a list of permitted scopes for the supplied product.
   *
   * Consumers should favour {@link batchedGetExtensibleProductPermission}, to batch
   * multiple scopes calls into one. {@link getProductScopes} may be used when a caller
   * explicitly wants a separate REST call to fetch scopes. One example may be if a product
   * has different scopes caching rules to other products.
   *
   * @param productId the product id to lookup scopes for
   * @param experience the experience this UI represents
   * @returns a promise of a list of scopes for the supplied product
   */
  async getProductScopes(productId, experience) {
    const productScopeMap = await this.getGenericProductPermissions({
      productIds: [productId],
      experience
    });
    return productScopeMap.get(productId) || [];
  }

  /**
   * Returns a list of {@link Products} which the user should see in the dialog
   *
   * @deprecated consumers should use {@link batchedGetExtensibleProductPermission} to fetch product scopes, or
   * {@link getProductScopes} to force a separate call to fetch a single product's scopes.
   *
   */
  async getProductPermissions(products, experience) {
    const lowercaseProductIds = products.map(p => p.toLowerCase());
    const productScopeMap = await this.getGenericProductPermissions({
      productIds: lowercaseProductIds,
      experience
    });
    return lowercaseProductIds.filter(product => {
      var _productScopeMap$get;
      return (_productScopeMap$get = productScopeMap.get(product)) === null || _productScopeMap$get === void 0 ? void 0 : _productScopeMap$get.length;
    }).map(product => product.toUpperCase());
  }
  async search({
    query,
    context,
    scopes,
    modelParams,
    resultLimit,
    filters = [],
    experience,
    sites = []
  }) {
    const cloudId = this.cloudId;
    const cloudIdsToFilterOn = sites.length > 0 ? sites : limitToMaxSiteUsage(this.siteMasterList).map(site => site.cloudId);
    const body = {
      query: query,
      cloudId,
      limit: resultLimit,
      scopes,
      filters: filters,
      searchSession: context,
      ...(modelParams.length > 0 ? {
        modelParams
      } : {}),
      experience,
      cloudIds: cloudIdsToFilterOn
    };
    const {
      result: response,
      durationMs: requestDurationMs
    } = await timed(this.makePostRequest(AggregatorClient.QUICKSEARCH_API_URL, body));
    return {
      response: {
        rawData: response,
        retrieveScope: scope =>
        // @ts-expect-error TS2367: This comparison appears to be unintentional because the types 'Scope' and 'SingleScope' have no overlap
        response.scopes.find(s => s.id === scope) || null
      },
      requestDurationMs
    };
  }
}
// Static URLs
_defineProperty(AggregatorClient, "EXPERIMENT_URL_PATH", 'experiment/v1');
_defineProperty(AggregatorClient, "QUICKSEARCH_API_URL", 'quicksearch/v1');